import React, { useEffect, useRef } from 'react';

/**
 * ColorAura
 *
 * Wraps any element with a soft, drifting, color-cycling aura behind it.
 * No defined shape — just blurred blobs of color floating around the element.
 *
 * Props:
 *   children     - the element to wrap (button, card, anything)
 *   palette      - array of [r,g,b] tuples; blobs cycle through these continuously
 *   speed        - animation speed multiplier. default 1
 *   colorSpeed   - how fast each blob cycles through the palette. default 0.15
 *   blobSize     - base radius of each blob in px. default 100
 *   spread       - how far blobs drift from center (multiplier). default 1
 *   blur         - CSS blur in px applied to the whole canvas. default 40
 *   bleed        - how far the canvas extends beyond the wrapped element (px). default 120
 *   className    - optional class on the outer wrapper
 *   style        - optional inline style on the outer wrapper
 */
export default function OrganicAnimation({
  children,
  palette = [
    [255, 107, 157], // pink
    [192, 110, 255], // purple
    [0, 217, 255],  // blue
    [0, 255, 170],  // green
    [255, 216, 107], // gold
    [255, 140, 100], // coral
  ],
  speed = 1,
  colorSpeed = 0.15,
  blobSize = 100,
  spread = 1,
  blur = 40,
  bleed = 120,
  className,
  style,
}) {
  const wrapperRef = useRef(null);
  const canvasRef = useRef(null);
  const targetRef = useRef(null);
  const rafRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const wrapper = wrapperRef.current;
    const target = targetRef.current;
    if (!canvas || !wrapper || !target) return;

    const ctx = canvas.getContext('2d');

    const resize = () => {
      const dpr = window.devicePixelRatio || 1;
      const targetRect = target.getBoundingClientRect();
      const w = targetRect.width + bleed * 2;
      const h = targetRect.height + bleed * 2;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = w + 'px';
      canvas.style.height = h + 'px';
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();

    const ro = new ResizeObserver(resize);
    ro.observe(target);

    const getColor = (t) => {
      const n = palette.length;
      const scaled = ((t % 1) + 1) % 1 * n;
      const i = Math.floor(scaled);
      const f = scaled - i;
      const c1 = palette[i % n];
      const c2 = palette[(i + 1) % n];
      const sf = f * f * (3 - 2 * f);
      return [
        Math.round(c1[0] + (c2[0] - c1[0]) * sf),
        Math.round(c1[1] + (c2[1] - c1[1]) * sf),
        Math.round(c1[2] + (c2[2] - c1[2]) * sf),
      ];
    };

    // Five blobs, each with its own orbit and offset into the color cycle
    const blobs = [
      { freq: 0.55, phase: 0, radius: 0.55, size: blobSize, colorOffset: 0.0 },
      { freq: 0.42, phase: 1.3, radius: 0.50, size: blobSize * 1.1, colorOffset: 0.2 },
      { freq: 0.63, phase: 2.6, radius: 0.60, size: blobSize * 0.95, colorOffset: 0.4 },
      { freq: 0.48, phase: 3.9, radius: 0.52, size: blobSize * 1.05, colorOffset: 0.6 },
      { freq: 0.57, phase: 5.2, radius: 0.58, size: blobSize, colorOffset: 0.8 },
    ];



    const startTime = performance.now();

    const draw = () => {
      const targetRect = target.getBoundingClientRect();
      const w = targetRect.width + bleed * 2;
      const h = targetRect.height + bleed * 2;
      const t = ((performance.now() - startTime) / 1000) * speed;

      ctx.clearRect(0, 0, w, h);

      const cx = w / 2;
      const cy = h / 2;

      ctx.globalCompositeOperation = 'lighter';

      // blobs.forEach((blob) => {
      //   const angle = t * blob.freq + blob.phase;
      //   const driftX = (Math.cos(angle) * blob.radius * 130 + Math.sin(angle * 1.7) * 30) * spread;
      //   const driftY = (Math.sin(angle * 0.8) * blob.radius * 80 + Math.cos(angle * 1.3) * 25) * spread;

      //   const x = cx + driftX;
      //   const y = cy + driftY;
      //   const size = blob.size + Math.sin(t * 1.2 + blob.phase) * 20;

      //   const [r, g, b] = getColor(t * colorSpeed + blob.colorOffset);

      //   const grad = ctx.createRadialGradient(x, y, 0, x, y, size);
      //   grad.addColorStop(0, `rgba(${r}, ${g}, ${b}, 0.55)`);
      //   grad.addColorStop(0.5, `rgba(${r}, ${g}, ${b}, 0.20)`);
      //   grad.addColorStop(1, `rgba(${r}, ${g}, ${b}, 0)`);

      //   ctx.fillStyle = grad;
      //   ctx.beginPath();
      //   ctx.arc(x, y, size, 0, Math.PI * 2);
      //   ctx.fill();
      // });

      blobs.forEach((blob) => {
        const angle = t * blob.freq + blob.phase;
        const driftX = (Math.cos(angle) * blob.radius * 130 + Math.sin(angle * 1.7) * 30) * spread;
        const driftY = (Math.sin(angle * 0.8) * blob.radius * 80 + Math.cos(angle * 1.3) * 25) * spread;

        const x = cx + driftX;
        const y = cy + driftY;
        const size = blob.size + Math.sin(t * 1.2 + blob.phase) * 20;

        const [r, g, b] = getColor(t * colorSpeed + blob.colorOffset);

        const grad = ctx.createRadialGradient(x, y, 0, x, y, size);
        grad.addColorStop(0, `rgba(${r}, ${g}, ${b}, 0.55)`);
        grad.addColorStop(0.5, `rgba(${r}, ${g}, ${b}, 0.2)`);
        grad.addColorStop(1, `rgba(${r}, ${g}, ${b}, 0)`);

        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.arc(x, y, size, 0, Math.PI * 2);
        ctx.fill();
      });

      rafRef.current = requestAnimationFrame(draw);
    };

    rafRef.current = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(rafRef.current);
      ro.disconnect();
    };
  }, [palette, speed, colorSpeed, blobSize, spread, blur, bleed]);

  return (
    <div
      ref={wrapperRef}
      className={className}
      style={{
        position: 'relative',
        display: 'inline-block',
        ...style,
      }}
    >
      <canvas
        ref={canvasRef}
        style={{
          position: 'absolute',
          top: -bleed,
          left: -bleed,
          pointerEvents: 'none',
          filter: `blur(${blur}px)`,
        }}
      />
      <div ref={targetRef} style={{ position: 'relative', zIndex: 1 }}>
        {children}
      </div>
    </div>
  );
}